if (!customElements.get('sc-promo-strip')) {
  class SCPromoStrip extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      if (this.dataset.sticky === 'true') {
        this.stickyObserver = new ResizeObserver(() => this.updateStickyOffset());
        this.connectStickyHeader();
        window.addEventListener('on:breakpoint-change', () => this.updateStickyOffset(), { signal: this.events.signal });
        this.addEventListener('focusin', () => this.header?.show?.(), { signal: this.events.signal });
        if (Shopify.designMode) {
          ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((event) => {
            document.addEventListener(event, () => {
              cancelAnimationFrame(this.stickyFrame);
              this.stickyFrame = requestAnimationFrame(() => this.connectStickyHeader());
            }, { signal: this.events.signal });
          });
        }
      }
      this.endTime = Number(this.dataset.endTime);
      this.codeButton = this.querySelector('[data-copy-code]');
      this.copyStatus = this.querySelector('.sc-promo-strip__copied');
      this.countdown = this.querySelector('.sc-promo-strip__countdown');
      this.endText = this.querySelector('.sc-promo-strip__end-text');
      this.units = Object.fromEntries([...this.querySelectorAll('[data-unit]')]
        .map((element) => [element.dataset.unit, element]));
      if (this.codeButton) {
        this.codeButton.disabled = !navigator.clipboard?.writeText;
        this.codeButton.addEventListener('click', () => this.copyCode(), { signal: this.events.signal });
      }
      if (this.endTime > 0 && this.countdown) {
        this.updateCountdown();
        if (Date.now() < this.endTime) this.timer = setInterval(() => this.updateCountdown(), 1000);
      }
      this.updateHeader();
    }

    disconnectedCallback() {
      this.events.abort();
      this.stickyObserver?.disconnect();
      cancelAnimationFrame(this.stickyFrame);
      this.headerSection?.style.removeProperty('--sc-sticky-promo-height');
      this.header?.setHeaderEnd?.();
      clearInterval(this.timer);
      clearTimeout(this.copyTimer);
    }

    // CSS owns stickiness and hide-on-scroll; only the two section heights need JS.
    connectStickyHeader() {
      this.stickyObserver.disconnect();
      this.headerSection?.style.removeProperty('--sc-sticky-promo-height');
      this.header?.setHeaderEnd?.();
      this.section = this.closest('.sc-promo-strip-section');
      this.header = this.section?.previousElementSibling?.querySelector('store-header[data-is-sticky]');
      this.headerSection = this.header?.closest('.shopify-section');
      this.stickyHeaderHeight = null;
      this.stickyPromoHeight = null;
      if (this.headerSection) {
        this.stickyObserver.observe(this.headerSection, { box: 'border-box' });
        this.stickyObserver.observe(this, { box: 'border-box' });
      }
      this.updateStickyOffset();
    }

    updateStickyOffset() {
      if (!this.section) return;
      // Round both heights: a fractional sticky offset makes the strip's background
      // and its contents round to device pixels independently, which shows up as an
      // uneven gap above and below the row at some scroll positions and zoom levels.
      const headerHeight = Math.round(this.headerSection?.getBoundingClientRect().height || 0);
      const promoHeight = this.dataset.sticky === 'true' && (theme.mediaMatches.md || this.dataset.stickyMobile === 'true')
        ? Math.round(this.getBoundingClientRect().height) : 0;
      if (headerHeight === this.stickyHeaderHeight && promoHeight === this.stickyPromoHeight) return;
      if (headerHeight !== this.stickyHeaderHeight) {
        this.section.style.setProperty('--sc-sticky-header-height', `${headerHeight}px`);
        this.stickyHeaderHeight = headerHeight;
      }
      if (promoHeight !== this.stickyPromoHeight) {
        this.headerSection?.style.setProperty('--sc-sticky-promo-height', `${promoHeight}px`);
        this.stickyPromoHeight = promoHeight;
      }
      this.header?.setHeaderEnd?.();
    }

    async copyCode() {
      clearTimeout(this.copyTimer);
      this.copyStatus.classList.remove('is-visible', 'is-error');
      try {
        await navigator.clipboard.writeText(this.codeButton.dataset.copyCode);
        if (!this.isConnected) return;
        this.copyStatus.textContent = this.copyStatus.dataset.success;
      } catch {
        if (!this.isConnected) return;
        this.copyStatus.textContent = this.copyStatus.dataset.error;
        this.copyStatus.classList.add('is-error');
      }
      this.copyStatus.classList.add('is-visible');
      this.copyTimer = setTimeout(() => {
        this.copyStatus.classList.remove('is-visible', 'is-error');
        this.copyStatus.textContent = '';
      }, 2500);
    }

    updateCountdown() {
      const remaining = Math.max(0, Math.ceil((this.endTime - Date.now()) / 1000));
      const values = {
        days: Math.floor(remaining / 86400),
        hours: Math.floor(remaining / 3600) % 24,
        minutes: Math.floor(remaining / 60) % 60,
        seconds: remaining % 60
      };
      Object.entries(values).forEach(([unit, value]) => {
        if (this.units[unit]) this.units[unit].textContent = String(value).padStart(2, '0');
      });
      if (!remaining) {
        clearInterval(this.timer);
        this.countdown.hidden = true;
        if (this.endText) this.endText.hidden = false;
        if (this.dataset.hideWhenEnded === 'true') this.hidden = true;
        this.updateHeader();
      }
    }

    updateHeader() {
      const header = document.querySelector('store-header');
      header?.updateHeaderHeights?.();
      header?.setHeaderEnd?.();
    }
  }
  customElements.define('sc-promo-strip', SCPromoStrip);
}
